import React from "react";
import { Box } from "@mui/material";
import { Chip } from "@mui/joy";
import { useTranslation } from "react-i18next";
import { DescriptionRounded } from "@mui-symbols-material/w300";
import { IOT_COMMISSIONING_DOCUMENTS } from "../../config/iotCommissioningDocuments";
import { useAuthenticatedFileOpen } from "../../hooks/useAuthenticatedFileOpen";

interface Props {
    /** Attribute index of the check — the key into IOT_COMMISSIONING_DOCUMENTS. */
    attributeIndex: string;
}

/**
 * Reference documents of one commissioning check as chips; nothing when none are mapped.
 * The PDFs need the user's JWT, so a chip fetches its document on click instead of linking to it.
 */
const CheckDocuments = ({ attributeIndex }: Props) => {
    const { t } = useTranslation();
    const openFile = useAuthenticatedFileOpen();
    const documents = IOT_COMMISSIONING_DOCUMENTS[attributeIndex] ?? [];
    if (documents.length === 0) return null;
    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mt: "8px" }} data-testid={`commissioning-doc-${attributeIndex}`}>
            {documents.map((document) => {
                // Document titles stay English like the PDFs themselves; the number is only the fallback label.
                const label = document.label ?? t("Document # {{number}}", { number: document.number });
                const icon = <DescriptionRounded sx={{ fontSize: 16 }} />;
                const url = document.url;
                return url ? (
                    <Chip
                        key={document.number}
                        size="sm"
                        variant="outlined"
                        startDecorator={icon}
                        // The card is a <label>: keep the click from toggling the check.
                        onClick={(event: React.MouseEvent) => {
                            event.preventDefault();
                            void openFile(url, label);
                        }}
                    >
                        {label}
                    </Chip>
                ) : (
                    <Chip key={document.number} size="sm" variant="soft" color="neutral" startDecorator={icon}>
                        {label}
                    </Chip>
                );
            })}
        </Box>
    );
};

export default CheckDocuments;
